import { Box, Typography, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import * as styles from './EmptyState.styles';

interface EmptyStateProps {
  message?: string;
}

// Dark mode illustration assets
const darkModeAssets = {
  imgEllipse5: '/assets/empty-state/dark/ellipse-5.svg',
  imgEllipse4: '/assets/empty-state/dark/ellipse-4.svg',
  imgEllipse1: '/assets/empty-state/dark/ellipse-1.svg',
  imgMaskGroup: '/assets/empty-state/dark/mask-group.svg',
  imgEllipse3: '/assets/empty-state/dark/ellipse-3.svg',
  imgVector: '/assets/empty-state/dark/vector.svg',
  imgVector1: '/assets/empty-state/dark/vector-1.svg',
  imgVector2: '/assets/empty-state/dark/vector-2.svg',
};

// Light mode illustration assets
const lightModeAssets = {
  imgEllipse5: '/assets/empty-state/light/ellipse-5.svg',
  imgEllipse4: '/assets/empty-state/light/ellipse-4.svg',
  imgEllipse1: '/assets/empty-state/light/ellipse-1.svg',
  imgMaskGroup: '/assets/empty-state/light/mask-group.svg',
  imgEllipse3: '/assets/empty-state/light/ellipse-3.svg',
  imgVector: '/assets/empty-state/light/vector.svg',
  imgVector1: '/assets/empty-state/light/vector-1.svg',
  imgVector2: '/assets/empty-state/light/vector-2.svg',
};

interface CustomEmptyIllustrationProps {
  mode?: 'Light' | 'Dark';
  size?: 'Large' | 'Small';
}

const CustomEmptyIllustration: React.FC<CustomEmptyIllustrationProps> =
  React.memo(({ mode = 'Dark', size = 'Large' }) => {
    // Choose the appropriate asset set based on mode
    const assets = mode === 'Light' ? lightModeAssets : darkModeAssets;

    return (
      <Box sx={styles.illustrationContainer}>
        {/* Ellipse 5 - top right small circle */}
        <Box sx={styles.ellipseContainer('76.569%', '93.891%', 0, '15.481%')}>
          <img alt="" style={styles.imageStyle} src={assets.imgEllipse5} />
        </Box>

        {/* Ellipse 4 - bottom center-right circle */}
        <Box sx={styles.ellipseContainer('4.603%', '56.27%', '18.006%', '61.506%')}>
          <img alt="" style={styles.imageStyle} src={assets.imgEllipse4} />
        </Box>

        {/* Ellipse 1 - large central circle */}
        <Box sx={styles.ellipseContainer(0, '16.72%', '22.508%', '20.921%')}>
          <img alt="" style={styles.imageStyle} src={assets.imgEllipse1} />
        </Box>

        {/* Mask group - main flowing shape */}
        <Box sx={styles.maskGroupContainer(mode)}>
          <img alt="" style={styles.imageStyle} src={assets.imgMaskGroup} />
        </Box>

        {/* Ellipse 3 - small top-left circle */}
        <Box sx={styles.ellipseContainer('66.109%', '23.151%', '62.379%', '15.063%')}>
          <img alt="" style={styles.imageStyle} src={assets.imgEllipse3} />
        </Box>

        {/* Vector - small bottom-right sparkle */}
        <Box sx={styles.ellipseContainer('31.381%', '90.675%', '3.859%', '61.506%')}>
          <img alt="" style={styles.imageStyle} src={assets.imgVector} />
        </Box>

        {/* Vector1 - small top-center sparkle */}
        <Box sx={styles.ellipseContainer('93.305%', '40.514%', '54.341%', 0)}>
          <img alt="" style={styles.imageStyle} src={assets.imgVector1} />
        </Box>

        {/* Vector2 - small bottom-left sparkle */}
        <Box sx={styles.ellipseContainer('12.971%', 0, '92.926%', '77.824%')}>
          <img alt="" style={styles.imageStyle} src={assets.imgVector2} />
        </Box>
      </Box>
    );
  });

export const EmptyState: React.FC<EmptyStateProps> = React.memo(
  ({ message = 'Data coming soon...' }) => {
    const theme = useTheme();

    // Memoize the illustration component with theme-based mode
    const illustrationComponent = useMemo(
      () => (
        <Box sx={styles.illustrationWrapper}>
          <CustomEmptyIllustration
            mode={theme.palette.mode === 'dark' ? 'Dark' : 'Light'}
            size="Large"
          />
        </Box>
      ),
      [theme.palette.mode],
    );

    return (
      <Box sx={styles.container}>
        {/* Theme-aware Figma Empty Illustration */}
        {illustrationComponent}

        <Typography variant="h6" color="text.primary" sx={styles.message}>
          {message}
        </Typography>
      </Box>
    );
  },
);
